import React, { useEffect, useState } from 'react';
import { PlaidLinkOnSuccessMetadata, usePlaidLink } from 'react-plaid-link';
import Button from '../../../generic-components/Button';
import { IPostLinkData } from '../../../types';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { httpService } from '../../../services/http.service';
import { BASE_URL } from '../../../constants/endpoints';
import { useStore } from '../../../store';

interface IProps {
  handlePostLink: (data: IPostLinkData) => void;
  className: string;
  title: string;
  linkToken: string;
  isLoading: boolean;
  shouldInvertLoaderColor?: boolean;
  shouldAutoOpenLink: boolean;
}

const PLAID_EVENTS = {
  LINK_OPEN: 'OPEN',
  LINK_DONE: 'HANDOFF',
  LINK_EXIT: 'EXIT',
  LINK_ERROR: 'ERROR',
};

export const PlaidLink = ({
  handlePostLink, className, title, linkToken, isLoading, shouldInvertLoaderColor, shouldAutoOpenLink,
}: IProps) => {
  const [isPlaidLinkActive, setIsPlaidLinkActive] = useState<boolean>(false);
  const { user: { customerId } } = useStore(state => state);
  const onSuccess = async (publicToken: string, metaData: PlaidLinkOnSuccessMetadata) => {
    trackMixPanel('Close Plaid Connect Web flow', {
      'Connect successfully?': true,
      'Number of Accounts Linked?': metaData?.accounts?.length || 0,
      'Bank Name': metaData?.institution?.name || '',
    });
    handlePostLink({ publicToken, metaData });
  };

  const onExit = async (err: any, metaData: any) => {
    if (!err || !err?.error_code) return;
    const plaidErrorBody = {
      errorCode: err?.error_code,
      errorMessage: err?.error_message,
      institutionId: metaData?.institution?.institution_id,
      institutionName: metaData?.institution?.name,
      errorJSON: err?.errorJSON,
      requestId: metaData?.request_id,
    };
    await httpService.post(`${BASE_URL}/api/v1/user/${customerId}/linkToken/ACH/linkError`, { ...plaidErrorBody });
  };

  // PLAID LINK EVENTS

  const onEvent = (eventType: string) => {
    switch (eventType) {
      case PLAID_EVENTS.LINK_ERROR:
        trackMixPanel('Close Plaid Connect Web flow', {
          'Connect successfully?': false,
          'Number of Accounts Linked?': 0,
          'Bank Name': '',
        });
        break;
      case PLAID_EVENTS.LINK_OPEN:
        trackMixPanel('Open Plaid Connect Web flow');
        break;
      case PLAID_EVENTS.LINK_DONE:
        setTimeout(() => {
          setIsPlaidLinkActive(false);
        }, 2500);
        break;
      case PLAID_EVENTS.LINK_EXIT:
        setIsPlaidLinkActive(false);
        break;
      default:
        break;
    }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onEvent,
    onExit,
    linkCustomizationName: 'lili_app',
  });

  useEffect(() => {
    if (ready && linkToken && shouldAutoOpenLink) {
      open();
    }
  }, [ready]);

  return (
    <Button testId='plaid-link' shouldInvertLoaderColor={shouldInvertLoaderColor} isLoading={isLoading || isPlaidLinkActive} disable={isLoading} onClick={() => open()} className={className} id='plaid-link' title={title} />
  );
};
